import { useCallback, useEffect, useRef, useState } from 'react';
import type { TooltipPlacement } from 'antd/es/tooltip';

// `body` in this app is `position: absolute; inset: 0; overflow: hidden` (the
// dashboard scrolls internally via the active app layout, not the page itself).
// A popover with a fixed height can end up taller than the room available in
// whichever direction it opens. When that happens the overflowing part isn't
// just off-screen — it is physically clipped away by that `overflow: hidden`
// on body, and whatever app content sits underneath (e.g. the top app header)
// shows through the gap, looking like the popover is "hidden behind" it.
const APP_LAYOUT_SCROLL_CONTAINER_SELECTOR = '.app-layout__content, main.ant-layout-content';
const VIEWPORT_MARGIN_PX = 24;

export interface PopoverViewportPlacementResult {
  triggerRef: React.MutableRefObject<HTMLSpanElement | null>;
  placement: TooltipPlacement;
  maxHeight: number;
  recalculatePlacement: () => void;
}

// Picks whichever side (above/below the trigger) has more room, and clamps the
// popover's height to exactly that much room instead of a fixed value, so it
// never gets clipped by an `overflow: hidden` ancestor. Call `recalculatePlacement`
// when the popover opens; it's also re-run automatically on scroll while open,
// since the trigger's position relative to the viewport changes as the user scrolls.
export const usePopoverViewportPlacement = (open: boolean, maxHeightPx: number): PopoverViewportPlacementResult => {
  const [placement, setPlacement] = useState<TooltipPlacement>('bottomLeft');
  const [maxHeight, setMaxHeight] = useState(maxHeightPx);
  const triggerRef = useRef<HTMLSpanElement | null>(null);

  const recalculatePlacement = useCallback((): void => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const spaceAbove = rect.top - VIEWPORT_MARGIN_PX;
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN_PX;

    if (spaceBelow >= spaceAbove) {
      setPlacement('bottomLeft');
      setMaxHeight(Math.min(maxHeightPx, spaceBelow));
    } else {
      setPlacement('topLeft');
      setMaxHeight(Math.min(maxHeightPx, spaceAbove));
    }
  }, [maxHeightPx]);

  useEffect(() => {
    if (!open) return undefined;

    const scrollContainer = document.querySelector(APP_LAYOUT_SCROLL_CONTAINER_SELECTOR);
    if (!scrollContainer) return undefined;

    scrollContainer.addEventListener('scroll', recalculatePlacement, { passive: true });
    return (): void => scrollContainer.removeEventListener('scroll', recalculatePlacement);
  }, [open, recalculatePlacement]);

  return { triggerRef, placement, maxHeight, recalculatePlacement };
};
